import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { IconButton, styled } from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { validateAuth0ApiErrors } from "../../context/utilsAuth0";
import { handleExportAsCsv } from "../../networking";
import { IEventLogFilters } from "../EventLog/Filters/types";
import { LoadingModal } from "../LoadingModal/LoadingModal";

const StyledDownloadSimple = styled(DownloadSimpleIcon)(({ theme }) => ({
  color: theme.palette.text50.main,
}));

async function downloadCsvFromString(data: string) {
  // Convert the data to a Blob
  const blob = new Blob([data], { type: "text/csv" });

  // Create a URL for the Blob
  const url = URL.createObjectURL(blob);

  // Create a temporary anchor element
  const a = document.createElement("a");
  a.href = url;
  a.download = "data.csv";

  // Simulate a click on the anchor element to trigger the download
  a.click();

  // Release the object URL to free up memory
  URL.revokeObjectURL(url);
}

const handleClickDownloadAsCsv = async (stream: string | null) => {
  if (stream) {
    try {
      await downloadCsvFromString(stream);
    } catch (error) {
      toast.error("Failed to download CSV");
      console.error("There was an error downlading the CSV:", error);
    }
  }
};

const ExportButton = ({
  queryVariables,
  query,
}: {
  queryVariables: DownloadFilters | UploadsFilters | IEventLogFilters;
  query: "downloads" | "uploads" | "events";
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [controller, setController] = useState<AbortController | null>(null);

  const handleDownload = async () => {
    const newController = new AbortController();
    setController(newController);

    setShowLoadingModal(true);

    try {
      const response = await handleExportAsCsv(queryVariables, getAccessTokenSilently, query, newController.signal);

      validateAuth0ApiErrors(response, navigate);
      return typeof response.data === "string" ? response.data : "";
    } catch (error) {
      if (axios.isCancel(error)) {
        // The error was cancelled by the abort controller
        return "";
      }

      console.error("There was an error exporting the data:", error);
      toast.error("There was an error exporting the data");
      return "";
    } finally {
      setShowLoadingModal(false);
    }
  };

  const handleClick = async () => {
    const stream = await handleDownload();
    await handleClickDownloadAsCsv(stream);
  };

  const handleCancel = () => {
    controller?.abort();
    setShowLoadingModal(false);
  };

  return (
    <>
      <IconButton onClick={handleClick} data-testid="download">
        <StyledDownloadSimple size={24} />
      </IconButton>
      <LoadingModal handleCancel={handleCancel} showLoadingModal={showLoadingModal} text="Exporting table" />
    </>
  );
};

export { ExportButton };
